/**
 * Status Bar Component
 *
 * Displays item count statistics at the bottom of the folder view.
 * Shows total items, breakdown by type, and optional storage size.
 */

import { useMemo } from 'react';

import { formatBytes } from './utils';
import type { WorkspaceItem } from './types';

interface StatusBarProps {
  /** All items in the current folder */
  items: WorkspaceItem[];
  /** Filtered items count (after applying filters) */
  filteredCount: number;
  /** Show storage size if available */
  showStorage?: boolean;
}

/**
 * Status bar for folder view footer
 */
export function StatusBar({ items, filteredCount, showStorage = true }: StatusBarProps) {
  // Calculate breakdown by type
  const stats = useMemo(() => {
    const breakdown = items.reduce(
      (acc, item) => {
        if (item.type === 'table') acc.tables++;
        else if (item.type === 'linked_table') acc.linkedTables++;
        else if (item.type === 'folder') acc.folders++;

        // Sum storage bytes
        if (item.storageBytes) {
          acc.totalStorage += item.storageBytes;
        }

        return acc;
      },
      { tables: 0, linkedTables: 0, folders: 0, totalStorage: 0 }
    );

    return breakdown;
  }, [items]);

  // Build breakdown text
  const breakdownParts: string[] = [];
  if (stats.tables > 0) {
    breakdownParts.push(`${stats.tables} table${stats.tables === 1 ? '' : 's'}`);
  }
  if (stats.linkedTables > 0) {
    breakdownParts.push(`${stats.linkedTables} linked table${stats.linkedTables === 1 ? '' : 's'}`);
  }
  if (stats.folders > 0) {
    breakdownParts.push(`${stats.folders} folder${stats.folders === 1 ? '' : 's'}`);
  }

  const breakdownText = breakdownParts.length > 0 ? breakdownParts.join(', ') : 'No items';

  // Total items text
  const totalText =
    filteredCount === items.length
      ? `${items.length} item${items.length === 1 ? '' : 's'}`
      : `${filteredCount} of ${items.length} item${items.length === 1 ? '' : 's'}`;

  return (
    <div className="flex h-8 items-center justify-between rounded-b-lg border bg-muted/30 px-4 text-xs text-muted-foreground">
      <div className="flex items-center gap-4">
        <span>{totalText}</span>
        {breakdownParts.length > 0 && (
          <>
            <span className="text-muted-foreground/50">•</span>
            <span>{breakdownText}</span>
          </>
        )}
      </div>

      {showStorage && stats.totalStorage > 0 && (
        <span>{formatBytes(stats.totalStorage)}</span>
      )}
    </div>
  );
}
